import { FastAverageColor } from "fast-average-color";

export interface BrandColorResult {
  hex: string;
  rgb: [number, number, number];
  hsl: [number, number, number];
  isDark: boolean;
}

function rgbToHsl(r: number, g: number, b: number): [number, number, number] {
  r /= 255;
  g /= 255;
  b /= 255;
  const max = Math.max(r, g, b);
  const min = Math.min(r, g, b);
  let h: number,
    s: number,
    l = (max + min) / 2;

  if (max === min) {
    h = s = 0;
  } else {
    const d = max - min;
    s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
    switch (max) {
      case r:
        h = (g - b) / d + (g < b ? 6 : 0);
        break;
      case g:
        h = (b - r) / d + 2;
        break;
      case b:
        h = (r - g) / d + 4;
        break;
      default:
        h = 0;
    }
    h /= 6;
  }

  return [Math.round(h * 360), Math.round(s * 100), Math.round(l * 100)];
}

export async function extractBrandColor(
  imageUrl: string
): Promise<BrandColorResult | null> {
  try {
    const colorExtractor = new FastAverageColor();
    const logoImage = new Image();

    logoImage.crossOrigin = "anonymous";

    return new Promise((resolve, reject) => {
      logoImage.onload = () => {
        try {
          const dominantColor = colorExtractor.getColor(logoImage, {
            algorithm: "dominant",
            ignoredColor: [
              [255, 255, 255, 255, 50],
              [254, 254, 254, 255, 50],
              [253, 253, 253, 255, 50],
            ],
            defaultColor: [128, 128, 128, 255],
          });

          const rgbValues = dominantColor.value;
          const [r, g, b] = rgbValues;

          const brandColorResult: BrandColorResult = {
            hex: dominantColor.hex,
            rgb: [r, g, b],
            hsl: rgbToHsl(r, g, b),
            isDark: dominantColor.isDark,
          };

          resolve(brandColorResult);
        } catch (extractionError) {
          console.error("Color extraction failed:", extractionError);
          reject(extractionError);
        } finally {
          colorExtractor.destroy();
        }
      };

      logoImage.onerror = () => {
        console.error("Logo image loading failed:", imageUrl);
        colorExtractor.destroy();
        reject(new Error("Failed to load image"));
      };

      logoImage.src = imageUrl;
    });
  } catch (setupError) {
    console.error("Brand color extraction setup failed:", setupError);
    return null;
  }
}

export async function extractBrandColorFromDomain(
  domain: string
): Promise<BrandColorResult | null> {
  const proxyLogoUrl = `${
    import.meta.env.VITE_API_ENDPOINT
  }/proxy-logo/${domain}`;
  return extractBrandColor(proxyLogoUrl);
}

export function brandColorToCss(
  brandColor: BrandColorResult,
  format: "hex" | "rgb" | "hsl" = "hex"
): string {
  switch (format) {
    case "hex":
      return brandColor.hex;
    case "rgb":
      return `rgb(${brandColor.rgb.join(", ")})`;
    case "hsl":
      return `hsl(${brandColor.hsl[0]}, ${brandColor.hsl[1]}%, ${brandColor.hsl[2]}%)`;
    default:
      return brandColor.hex;
  }
}
